/** Sidebar, top bar, barangay switcher and the permission-aware menu. */
import { useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { guideFor } from './help/guides';
import {
  FileBarChart,
  Globe,
  MessageSquare,
  UserCheck,
  HandHeart,
  ListChecks,
  PackageOpen,
  Tent,
  Waves,
  ClipboardCheck,
  HardHat,
  Package,
  ShoppingCart,
  BookMarked,
  CalendarDays,
  Network,
  UsersRound,
  BookOpen,
  Coins,
  FileSignature,
  Landmark,
  Megaphone,
  PiggyBank,
  Receipt,
  Ticket,
  Wallet,
  HeartHandshake,
  ShieldAlert,
  CircleHelp,
  Gavel,
  NotebookPen,
  Scale,
  Siren,
  Building,
  Building2,
  Lock,
  Home,
  FileText,
  Store,
  BarChart3,
  Upload,
  ChevronDown,
  Download,
  FlaskConical,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Menu,
  Monitor,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  ScrollText,
  Settings,
  ShieldCheck,
  Sun,
  User,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { applyUpdate, useInstallPrompt, useUpdateReady } from '../lib/pwa';
import { useTheme } from '../lib/theme';
import { useSidebarCollapsed } from '../lib/sidebar';
import { Button } from '../components/ui';

interface NavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
  /** Any one of these is enough to see the item. */
  permissions?: string[];
  /** ITCSI platform administrators only. */
  platform?: boolean;
  /** Hidden when the user has any of these (the item is already in a submenu for them). */
  hideWith?: string[];
  /** The Punong Barangay, or anyone holding a restricted-module grant. */
  grantOrPunong?: boolean;
  /** Restricted module: shown only to holders of this grant. */
  grant?: string;
  children?: NavItem[];
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', Icon: LayoutDashboard, permissions: ['dashboard.view'] },
  {
    to: '/residents',
    label: 'Residents',
    Icon: Users,
    permissions: ['residents.view'],
    children: [
      { to: '/residents', label: 'Residents', Icon: Users, permissions: ['residents.view'] },
      { to: '/households', label: 'Households', Icon: Home, permissions: ['residents.view'] },
      { to: '/registry/reports', label: 'Registry Reports', Icon: BarChart3, permissions: ['residents.view', 'reports.view'] },
      { to: '/registry/import', label: 'Import', Icon: Upload, permissions: ['residents.import'] },
    ],
  },
  {
    to: '/documents',
    label: 'Certificates',
    Icon: FileText,
    permissions: ['documents.request', 'documents.approve', 'documents.release', 'business.view'],
    children: [
      { to: '/documents', label: 'Requests', Icon: FileText, permissions: ['documents.request', 'documents.approve', 'documents.release'] },
      { to: '/businesses', label: 'Businesses', Icon: Store, permissions: ['business.view', 'business.manage'] },
    ],
  },
  {
    to: '/blotter',
    label: 'Peace & Order',
    Icon: Scale,
    permissions: ['blotter.view', 'blotter.create', 'kp.view'],
    children: [
      { to: '/blotter', label: 'Blotter', Icon: NotebookPen, permissions: ['blotter.view', 'blotter.create'] },
      { to: '/tanod', label: 'Tanod Duty', Icon: Siren, permissions: ['blotter.view', 'blotter.create'] },
      { to: '/kp', label: 'Lupon Cases', Icon: Gavel, permissions: ['kp.view'] },
      { to: '/kp/report', label: 'Lupon Report', Icon: BarChart3, permissions: ['kp.manage'] },
    ],
  },
  {
    to: '/portal-admin/accounts',
    label: 'Citizen Portal',
    Icon: Globe,
    permissions: ['portal.manage', 'documents.request', 'documents.release'],
    children: [
      { to: '/portal-admin/accounts', label: 'Online Accounts', Icon: UserCheck, permissions: ['portal.manage'] },
      { to: '/portal-admin/announcements', label: 'Announcements & SMS', Icon: Megaphone, permissions: ['portal.manage'] },
      { to: '/portal-admin/feedback', label: 'Feedback', Icon: MessageSquare, permissions: ['portal.manage', 'documents.request', 'documents.release'] },
    ],
  },
  {
    to: '/setup/barangay',
    label: 'Setup',
    Icon: Settings,
    permissions: ['barangay.view', 'barangay.manage', 'officials.view', 'users.view', 'audit.view'],
    children: [
      { to: '/setup/barangay', label: 'Barangay Profile', Icon: Building2, permissions: ['barangay.view', 'barangay.manage'] },
      { to: '/setup/officials', label: 'Officials & Staff', Icon: Users, permissions: ['officials.view', 'officials.manage'] },
      { to: '/setup/documents', label: 'Documents & Fees', Icon: FileText, permissions: ['barangay.manage'] },
      { to: '/setup/users', label: 'Users', Icon: User, permissions: ['users.view', 'users.manage'] },
      { to: '/setup/roles', label: 'Roles & Permissions', Icon: ShieldCheck, permissions: ['users.view', 'users.manage'] },
      { to: '/setup/support', label: 'ITCSI Support Access', Icon: LifeBuoy, permissions: ['users.view', 'users.manage'] },
      { to: '/setup/audit', label: 'Audit Log', Icon: ScrollText, permissions: ['audit.view'] },
      { to: '/privacy', label: 'Data Privacy', Icon: ShieldCheck, permissions: ['privacy.manage'] },
    ],
  },
  { to: '/setup/officials', label: 'Officials & Staff', Icon: Users, permissions: ['officials.view'], hideWith: ['barangay.view', 'users.view'] },
  {
    to: '/social/programs',
    label: 'Social Services',
    Icon: HandHeart,
    permissions: ['social.view', 'social.manage'],
    children: [
      { to: '/social/programs', label: 'Programs', Icon: ListChecks, permissions: ['social.view', 'social.manage'] },
      { to: '/social/assistance', label: 'Assistance', Icon: HandHeart, permissions: ['social.view', 'social.manage'] },
      { to: '/social/distributions', label: 'Distributions', Icon: PackageOpen, permissions: ['social.view', 'social.manage'] },
    ],
  },
  {
    to: '/drrm/incidents',
    label: 'DRRM',
    Icon: LifeBuoy,
    permissions: ['drrm.view', 'drrm.manage'],
    children: [
      { to: '/drrm/incidents', label: 'Incidents & Evacuation', Icon: Siren, permissions: ['drrm.view', 'drrm.manage'] },
      { to: '/drrm/zones', label: 'Hazard Zones', Icon: Waves, permissions: ['drrm.view', 'drrm.manage'] },
      { to: '/drrm/centers', label: 'Evacuation Centers', Icon: Tent, permissions: ['drrm.view', 'drrm.manage'] },
      { to: '/drrm/fund', label: 'BDRRM Fund', Icon: Wallet, permissions: ['drrm.view', 'drrm.manage'] },
    ],
  },
  {
    to: '/legislation',
    label: 'Legislation',
    Icon: BookMarked,
    permissions: ['legislation.view', 'legislation.manage'],
    children: [
      { to: '/legislation', label: 'Ordinances & Resolutions', Icon: BookMarked, permissions: ['legislation.view', 'legislation.manage'] },
      { to: '/legislation/sessions', label: 'Sessions', Icon: CalendarDays, permissions: ['legislation.view', 'legislation.manage'] },
      { to: '/legislation/assemblies', label: 'Barangay Assembly', Icon: UsersRound, permissions: ['legislation.view', 'legislation.manage'] },
      { to: '/legislation/committees', label: 'Committees', Icon: Network, permissions: ['legislation.view', 'legislation.manage'] },
    ],
  },
  {
    to: '/treasury',
    label: 'Treasury',
    Icon: Landmark,
    permissions: ['finance.view', 'finance.collect', 'finance.manage', 'finance.approve'],
    children: [
      { to: '/treasury', label: 'Collections', Icon: Receipt, permissions: ['finance.view', 'finance.collect', 'finance.manage'] },
      { to: '/treasury/deposits', label: 'Deposits', Icon: PiggyBank, permissions: ['finance.view', 'finance.manage', 'finance.collect'] },
      { to: '/treasury/cashbook', label: 'Cashbook', Icon: BookOpen, permissions: ['finance.view', 'finance.manage'] },
      { to: '/treasury/budget', label: 'Budget', Icon: Wallet, permissions: ['finance.view', 'finance.manage', 'finance.approve'] },
      { to: '/treasury/vouchers', label: 'Vouchers', Icon: FileSignature, permissions: ['finance.view', 'finance.manage', 'finance.approve'] },
      { to: '/treasury/income', label: 'Income & Advances', Icon: Coins, permissions: ['finance.view', 'finance.manage'] },
      { to: '/treasury/booklets', label: 'OR Booklets', Icon: Ticket, permissions: ['finance.view', 'finance.collect', 'finance.manage'] },
      { to: '/treasury/fdp', label: 'Full Disclosure', Icon: Megaphone, permissions: ['finance.view', 'finance.manage', 'finance.approve', 'portal.manage'] },
    ],
  },
  {
    to: '/projects',
    label: 'Projects & Property',
    Icon: HardHat,
    permissions: ['projects.view', 'projects.manage', 'property.view', 'property.manage'],
    children: [
      { to: '/projects', label: 'Projects', Icon: HardHat, permissions: ['projects.view', 'projects.manage'] },
      { to: '/procurement', label: 'Procurement', Icon: ShoppingCart, permissions: ['projects.view', 'projects.manage'] },
      { to: '/property', label: 'Property', Icon: Package, permissions: ['property.view', 'property.manage'] },
      { to: '/property/counts', label: 'Physical Count', Icon: ClipboardCheck, permissions: ['property.view', 'property.manage'] },
    ],
  },
  {
    to: '/vaw',
    label: 'VAW Desk',
    Icon: HeartHandshake,
    grant: 'vaw.access',
    children: [
      { to: '/vaw', label: 'Cases', Icon: HeartHandshake, grant: 'vaw.access' },
      { to: '/vaw/report', label: 'Quarterly Report', Icon: BarChart3, grant: 'vaw.access' },
    ],
  },
  {
    to: '/badac',
    label: 'BADAC',
    Icon: ShieldAlert,
    grant: 'badac.access',
    children: [
      { to: '/badac', label: 'Plan & Records', Icon: ShieldAlert, grant: 'badac.access' },
      { to: '/badac/report', label: 'Monthly Report', Icon: BarChart3, grant: 'badac.access' },
    ],
  },
  { to: '/restricted-access', label: 'Restricted Access', Icon: Lock, grantOrPunong: true },
  { to: '/platform/barangays', label: 'Barangays', Icon: Building, platform: true },
  { to: '/platform/consolidated', label: 'Consolidated View', Icon: BarChart3, platform: true },
  { to: '/reports', label: 'Report Library', Icon: FileBarChart },
  { to: '/help', label: 'Help', Icon: CircleHelp },
];

/** Always-visible notices: a sandbox is not the real books; a support seat is watched. */
function Banners(): JSX.Element | null {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <>
      {user.sandbox && (
        <div role="status" className="mb-4 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
          <FlaskConical aria-hidden="true" className="h-4 w-4 shrink-0" />
          Sandbox — test data only. Documents printed here are watermarked and not valid.
        </div>
      )}
      {user.supportSessionId && (
        <div role="status" className="mb-4 flex items-center gap-2 rounded-xl border border-brand/40 bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 dark:bg-slate-800 dark:text-brand-100">
          <LifeBuoy aria-hidden="true" className="h-4 w-4 shrink-0" />
          ITCSI support session: read-only{user.supportEndsAt ? `, ends ${new Date(user.supportEndsAt).toLocaleTimeString('en-PH', { timeZone: 'Asia/Manila', hour: 'numeric', minute: '2-digit' })}` : ''}. Every page you open is logged for the client.
        </div>
      )}
    </>
  );
}

function NavTree({
  onNavigate,
  collapsed = false,
}: {
  onNavigate?: () => void;
  /** Icons only. Submenus are hidden: a parent's own link is its landing page. */
  collapsed?: boolean;
}): JSX.Element {
  const { can, user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState<string | null>(
    () => NAV.find((item) => item.children?.some((c) => location.pathname.startsWith(c.to)))?.to ?? null,
  );

  const visible = (item: NavItem): boolean =>
    item.platform ? Boolean(user?.isPlatformAdmin) : item.grant ? Boolean(user?.grants?.includes(item.grant as never)) : item.grantOrPunong ? Boolean(user?.companyId && (user.roleName === 'Punong Barangay' || (user.grants?.length ?? 0) > 0)) : (!item.permissions || can(...item.permissions)) && !(item.hideWith && can(...item.hideWith));

  return (
    <ul className="space-y-1">
      {NAV.filter(visible).map((item) => {
        const children = collapsed ? [] : (item.children ?? []).filter(visible);
        const expanded = open === item.to;
        return (
          <li key={item.to}>
            <div className="flex items-center">
              <NavLink
                to={item.to}
                end={item.to === '/'}
                onClick={onNavigate}
                // The label is gone in the rail, so the name has to reach a
                // screen reader and a hovering mouse some other way.
                title={collapsed ? item.label : undefined}
                aria-label={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  `flex flex-1 items-center rounded-xl text-sm font-medium transition ${
                    collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2'
                  } ${
                    isActive
                      ? 'bg-navy text-white'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                  }`
                }
              >
                <item.Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                {!collapsed && item.label}
              </NavLink>
              {!collapsed && children.length > 0 && (
                <button
                  className="ml-1 grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  aria-label={expanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
                  aria-expanded={expanded}
                  onClick={() => setOpen(expanded ? null : item.to)}
                >
                  <ChevronDown
                    aria-hidden="true"
                    className={`h-4 w-4 transition ${expanded ? 'rotate-180' : ''}`}
                  />
                </button>
              )}
            </div>
            {children.length > 0 && expanded && (
              <ul className="ml-4 mt-1 space-y-1 border-l border-slate-200 pl-3 dark:border-slate-800">
                {children.map((child) => (
                  <li key={child.to}>
                    <NavLink
                      to={child.to}
                      // A parent-level page (e.g. /integrations) must not stay lit on its siblings.
                      end={children.some((other) => other !== child && other.to.startsWith(`${child.to}/`))}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        `block rounded-lg px-3 py-1.5 text-sm transition ${
                          isActive
                            ? 'bg-brand-50 font-semibold text-brand-700 dark:bg-slate-800 dark:text-brand-100'
                            : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                        }`
                      }
                    >
                      {child.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Switches the session to another company this user belongs to. */
function CompanySwitcher(): JSX.Element | null {
  const { user, companies, switchCompany } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (companies.length < 2) return null;
  return (
    <label className="flex min-w-0 items-center gap-2 text-sm">
      <span className="sr-only">Company</span>
      <select
        className="min-w-0 max-w-[16rem] truncate rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        value={user?.companyId ?? ''}
        disabled={busy}
        onChange={(event) => {
          setBusy(true);
          void switchCompany(event.target.value)
            .then(() => navigate('/', { replace: true }))
            .finally(() => setBusy(false));
        }}
      >
        {companies.map((c) => (
          <option key={c.companyId} value={c.companyId}>
            {c.companyName} — {c.roleName}
          </option>
        ))}
      </select>
    </label>
  );
}

export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const { user, signOut } = useAuth();
  const { choice, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const updateReady = useUpdateReady();
  const { canInstall, install } = useInstallPrompt();

  const onSignOut = async (): Promise<void> => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-slate-50 pb-safe dark:bg-slate-950">
      <aside
        className={`fixed inset-y-0 left-0 hidden flex-col border-r border-slate-200 bg-white py-5 transition-[width] duration-200 dark:border-slate-800 dark:bg-slate-900 md:flex ${
          collapsed ? 'w-16 px-2' : 'w-64 px-4'
        }`}
      >
        <div className={`mb-6 flex items-center ${collapsed ? 'justify-center' : 'justify-between px-1'}`}>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-lg font-bold text-navy dark:text-white">
                ITCSI <span className="text-brand">Barangay</span>
              </p>
              <p className="truncate text-xs text-slate-500">{user?.companyName ?? 'No company selected'}</p>
            </div>
          )}
          <button
            type="button"
            onClick={toggleCollapsed}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
            aria-label={collapsed ? 'Expand the menu' : 'Collapse the menu'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand the menu' : 'Collapse the menu'}
          >
            {collapsed ? (
              <PanelLeftOpen aria-hidden="true" className="h-5 w-5" />
            ) : (
              <PanelLeftClose aria-hidden="true" className="h-5 w-5" />
            )}
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto" aria-label="Main">
          <NavTree collapsed={collapsed} />
        </nav>
        <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-800">
          {!collapsed && (
            <>
              <NavLink to="/account" className="block truncate px-3 text-sm font-medium text-slate-700 hover:underline dark:text-slate-200" title="My account and sessions">
                {user?.name}
              </NavLink>
              <p className="truncate px-3 text-xs text-slate-500">{user?.roleName ?? (user?.isPlatformAdmin ? 'Platform admin' : '')}</p>
            </>
          )}
          <button
            onClick={() => void onSignOut()}
            title={collapsed ? `Sign out — ${user?.name ?? ''}` : undefined}
            aria-label={collapsed ? 'Sign out' : undefined}
            className={`mt-2 flex w-full items-center rounded-xl text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 ${
              collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2'
            }`}
          >
            <LogOut aria-hidden="true" className="h-4 w-4 shrink-0" />
            {!collapsed && 'Sign out'}
          </button>
        </div>
      </aside>

      <div
        className={`flex min-w-0 flex-1 flex-col transition-[margin] duration-200 ${
          collapsed ? 'md:ml-16' : 'md:ml-64'
        }`}
      >
        {updateReady && (
          <div className="flex flex-wrap items-center justify-between gap-2 bg-brand px-4 py-2 text-sm text-white">
            <span>A newer version of ITCSI Barangay is ready.</span>
            <button
              type="button"
              onClick={() => void applyUpdate()}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-white/15 px-3 font-semibold hover:bg-white/25"
            >
              <RefreshCw aria-hidden="true" className="h-4 w-4" /> Reload now
            </button>
          </div>
        )}

        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 pt-safe backdrop-blur dark:border-slate-800 dark:bg-slate-900/90">
          <button
            onClick={() => setMenuOpen((open) => !open)}
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 md:hidden"
            aria-label="Menu"
            aria-expanded={menuOpen}
          >
            <Menu aria-hidden="true" className="h-5 w-5" />
          </button>
          <span className="font-semibold text-navy dark:text-white md:hidden">ITCSI Barangay</span>
          <CompanySwitcher />
          <div className="flex-1" />
          <button
            onClick={() => { const t = guideFor(location.pathname); navigate(t ? `/help?topic=${t}` : '/help'); }}
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label="Help for this screen"
            title="Help for this screen"
          >
            <CircleHelp aria-hidden="true" className="h-4 w-4" />
          </button>
          {canInstall && (
            <button
              onClick={() => void install()}
              className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              aria-label="Install ITCSI Barangay on this device"
              title="Install on this device"
            >
              <Download aria-hidden="true" className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={toggle}
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label={`Theme: ${choice}. Click to change.`}
            title={`Theme: ${choice === 'system' ? 'follow system' : choice}`}
          >
            {choice === 'dark' ? (
              <Moon aria-hidden="true" className="h-4 w-4" />
            ) : choice === 'light' ? (
              <Sun aria-hidden="true" className="h-4 w-4" />
            ) : (
              <Monitor aria-hidden="true" className="h-4 w-4" />
            )}
          </button>
          <Button variant="ghost" size="sm" onClick={() => void onSignOut()} className="hidden md:inline-flex">
            <LogOut aria-hidden="true" className="h-4 w-4" /> Sign out
          </Button>
        </header>

        {menuOpen && (
          <div className="border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900 md:hidden">
            <NavTree onNavigate={() => setMenuOpen(false)} />
          </div>
        )}

        <main className="flex-1 px-4 py-6 md:px-6">
          <Banners />
          {children}
        </main>
      </div>
    </div>
  );
}
